import axios from 'axios';
import { API_BASE_URL } from '../utils/constants';
import { getAuthToken } from '../utils/storage';
import { extractApiError } from '../utils/apiError';

import { applyWarehouseFilter, warehouseParamsSerializer } from '../utils/warehouseParams';
import { registerClearable } from '../utils/cacheRegistry';

// Create axios instance for product sizes API calls
const productSizesAPI = axios.create({
  baseURL: `${API_BASE_URL}/api/v1`,
  headers: {
    'Content-Type': 'application/json',
  },
  paramsSerializer: warehouseParamsSerializer,
});

// Add request interceptor to include auth token
productSizesAPI.interceptors.request.use(
  (config) => {
    const token = getAuthToken();

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      console.warn('No auth token found in storage for productSizesAPI request');
    }
    return applyWarehouseFilter(config);
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle auth errors
productSizesAPI.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid - clear storage and redirect to login
      localStorage.removeItem('auth_token');
      localStorage.removeItem('access_token');
      localStorage.removeItem('user_data');
      sessionStorage.removeItem('auth_token');
      sessionStorage.removeItem('access_token');
      sessionStorage.removeItem('user_data');

      // Redirect to login page
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

const BASE_URL = '/product-size';

// Simple in-memory cache with optional sessionStorage persistence
const CACHE_PREFIX = 'erp_product_sizes_cache_v1';
const DEFAULT_TTL_MS = 10 * 60 * 1000; // 10 minutes

function cacheKey(key) {
  return `${CACHE_PREFIX}::${key}`;
}

function readCache(key) {
  try {
    const raw = sessionStorage.getItem(cacheKey(key));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.expires) return null;
    if (Date.now() > parsed.expires) {
      sessionStorage.removeItem(cacheKey(key));
      return null;
    }
    return parsed.value;
  } catch (err) {
    // If sessionStorage is not available (e.g. privacy mode), ignore and fallback to network
    return null;
  }
}

function writeCache(key, value, ttl = DEFAULT_TTL_MS) {
  try {
    const payload = JSON.stringify({ value, expires: Date.now() + ttl });
    sessionStorage.setItem(cacheKey(key), payload);
  } catch (err) {
    // ignore storage errors
  }
}

// Lightweight in-memory fallback (useful within single-page session)
const memoryCache = new Map();

function readMemoryCache(key) {
  const entry = memoryCache.get(key);
  if (!entry) return null;
  if (Date.now() > entry.expires) {
    memoryCache.delete(key);
    return null;
  }
  return entry.value;
}

function writeMemoryCache(key, value, ttl = DEFAULT_TTL_MS) {
  memoryCache.set(key, { value, expires: Date.now() + ttl });
}

function deleteCacheEntry(key) {
  try {
    memoryCache.delete(key);
  } catch (e) {
    // ignore
  }
  try {
    sessionStorage.removeItem(cacheKey(key));
  } catch (e) {
    // ignore
  }
}

/**
 * Apply a modifier function to every cached full-list (all::...) entry.
 * The modifier receives a copy of the array and should return the new array.
 */
// A cached full list is either a bare array or a paginated `{ items, total }`
// envelope (the newer list endpoints). The modifier used to run only on bare
// arrays, so an envelope kept serving the stale row after an update/delete (P1-4).
function applyListModifier(value, modifierFn) {
  if (Array.isArray(value)) return modifierFn(value.slice());
  if (value && Array.isArray(value.items)) {
    const items = modifierFn(value.items.slice());
    const delta = items.length - value.items.length;
    const total = typeof value.total === 'number' ? Math.max(0, value.total + delta) : value.total;
    return { ...value, items, total };
  }
  return null;
}

function updateFullListCachesWithModifier(modifierFn) {
  // Update memory cache entries
  try {
    for (const [key, entry] of memoryCache.entries()) {
      if (typeof key === 'string' && key.startsWith('all::')) {
        const next = applyListModifier(entry?.value, modifierFn);
        if (next !== null) writeMemoryCache(key, next);
      }
    }
  } catch (e) {
    // ignore
  }

  // Update sessionStorage entries
  try {
    for (let i = 0; i < sessionStorage.length; i++) {
      const sk = sessionStorage.key(i);
      if (!sk) continue;
      if (sk.startsWith(`${CACHE_PREFIX}::all::`)) {
        const raw = sessionStorage.getItem(sk);
        if (!raw) continue;
        try {
          const parsed = JSON.parse(raw);
          const next = applyListModifier(parsed?.value, modifierFn);
          if (next !== null) {
            parsed.value = next;
            sessionStorage.setItem(sk, JSON.stringify(parsed));
          }
        } catch (e) {
          // ignore malformed entry
        }
      }
    }
  } catch (e) {
    // ignore sessionStorage issues
  }
}


/**
 * Product Sizes Service - Handles all API calls related to product size management
 * - Exposes helpers for list, get, create, update, delete and stock update operations
 */
const productSizesService = {
  /**
   * Get all product sizes with optional filters
   * @param {Object} params - Query parameters
   * @param {number} params.skip - Number of records to skip (pagination)
   * @param {number} params.limit - Number of records to return
   * @param {string} params.search - Search term
   * @param {number} params.product_category_id - Filter by product category
   * @param {number} params.product_catalogue_id - Filter by product catalogue
   * @param {number} params.hsn_code_id - Filter by HSN code
   * @param {string} params.product_thickness - Filter by thickness
   * @param {number} params.min_stock - Minimum stock filter
   * @param {number} params.max_stock - Maximum stock filter
   * @param {Object} options - Additional options
   * @param {boolean} options.force - Force fresh data from server, bypass cache
   * @returns {Promise} API response with product sizes data
   */
  async getAllProductSizes(params = {}, options = {}) {
    const { force = false } = options;
    const key = `all::${JSON.stringify(params || {})}`;

    // Only use cache if not forced to refresh
    if (!force) {
      // Try memory cache first
      const mem = readMemoryCache(key);
      if (mem) return { success: true, data: mem };

      // Then sessionStorage cache
      const sess = readCache(key);
      if (sess) {
        // populate memory cache for faster subsequent reads
        writeMemoryCache(key, sess);
        return { success: true, data: sess };
      }
    }

    try {
      const queryParams = new URLSearchParams();
      if (params.search !== undefined && params.search !== '') {
        queryParams.append('search', params.search);
      }
      if (params.skip !== undefined) queryParams.append('skip', params.skip);
      if (params.limit !== undefined) queryParams.append('limit', params.limit);
      const queryStr = queryParams.toString();
      const response = await productSizesAPI.get(queryStr ? `${BASE_URL}?${queryStr}` : BASE_URL);
      const data = response.data;
      // cache results
      writeMemoryCache(key, data);
      writeCache(key, data);
      return {
        success: true,
        data,
      };
    } catch (error) {
      return {
        success: false,
        error: extractApiError(error),
      };
    }
  },

  /**
   * Get a specific product size by ID
   * @param {number|string} productSizeId - Product size ID
   * @param {Object} options - Additional options
   * @param {boolean} options.force - Force fresh data from server, bypass cache
   * @returns {Promise} API response with product size data
   */
  async getProductSizeById(productSizeId, options = {}) {
    const { force = false } = options;
    const key = `byId::${productSizeId}`;

    // Only use cache if not forced to refresh
    if (!force) {
      const mem = readMemoryCache(key);
      if (mem) return { success: true, data: mem };

      const sess = readCache(key);
      if (sess) {
        writeMemoryCache(key, sess);
        return { success: true, data: sess };
      }

      // As a fallback, try to find the product size in a cached full-list
      const fullKey = `all::${JSON.stringify({})}`;
      const fullMem = readMemoryCache(fullKey) || readCache(fullKey);
      if (fullMem && Array.isArray(fullMem)) {
        const found = fullMem.find((ps) => String(ps.id) === String(productSizeId));
        if (found) {
          writeMemoryCache(key, found);
          writeCache(key, found);
          return { success: true, data: found };
        }
      }
    }

    try {
      const response = await productSizesAPI.get(`${BASE_URL}/${productSizeId}`);
      const data = response.data;
      writeMemoryCache(key, data);
      writeCache(key, data);
      return {
        success: true,
        data,
      };
    } catch (error) {
      return {
        success: false,
        error: extractApiError(error),
      };
    }
  },

  /**
   * Create a new product size
   * @param {Object} productSizeData - Product size data
   * @param {string} productSizeData.alias_name - Alias name for the product size
   * @param {string} productSizeData.product_size - Size specification (e.g., "4x8 feet")
   * @param {number} productSizeData.product_weight - Weight of the product
   * @param {number} productSizeData.standard_length - Standard length
   * @param {string} productSizeData.product_thickness - Thickness specification
   * @param {number} productSizeData.current_stock - Current stock quantity
   * @param {number} productSizeData.minimum_stock - Minimum stock threshold
   * @param {number} productSizeData.product_category_id - Product category ID
   * @param {number} productSizeData.product_catalogue_id - Product catalogue ID
   * @param {number} productSizeData.hsn_code_id - HSN code ID
   * @returns {Promise} API response with created product size data
   */
  async createProductSize(productSizeData) {
    try {
      const response = await productSizesAPI.post(BASE_URL, productSizeData);
      const created = response.data;

      // Update cached full-lists by prepending the created product size
      updateFullListCachesWithModifier((arr) => {
        // Avoid duplicates
        const exists = arr.some((ps) => String(ps.id) === String(created.id));
        if (exists) return arr;
        return [created, ...arr];
      });

      // Also cache item by id
      try {
        const key = `byId::${created.id}`;
        writeMemoryCache(key, created);
        writeCache(key, created);
      } catch (e) { }

      return {
        success: true,
        data: created,
      };
    } catch (error) {
      return {
        success: false,
        error: extractApiError(error),
      };
    }
  },

  /**
   * Update an existing product size
   * @param {number|string} productSizeId - Product size ID
   * @param {Object} productSizeData - Updated product size data
   * @returns {Promise} API response with updated product size data
   */
  async updateProductSize(productSizeId, productSizeData) {
    try {
      const response = await productSizesAPI.put(`${BASE_URL}/${productSizeId}`, productSizeData);
      const updated = response.data;

      // Update cached full-lists by replacing the item
      updateFullListCachesWithModifier((arr) => {
        return arr.map((ps) => (String(ps.id) === String(productSizeId) ? updated : ps));
      });

      // Update byId cache
      try {
        const key = `byId::${productSizeId}`;
        writeMemoryCache(key, updated);
        writeCache(key, updated);
      } catch (e) { }

      return {
        success: true,
        data: updated,
      };
    } catch (error) {
      return {
        success: false,
        error: extractApiError(error),
      };
    }
  },

  /**
   * Delete a product size
   * @param {number|string} productSizeId - Product size ID
   * @returns {Promise} API response
   */
  async deleteProductSize(productSizeId) {
    try {
      const response = await productSizesAPI.delete(`${BASE_URL}/${productSizeId}`);

      // Remove from cached full-lists
      updateFullListCachesWithModifier((arr) => arr.filter((ps) => String(ps.id) !== String(productSizeId)));

      // Remove byId cache
      try {
        deleteCacheEntry(`byId::${productSizeId}`);
      } catch (e) { }

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: extractApiError(error),
      };
    }
  },

  /**
   * Delete multiple product sizes
   * @param {Array<number|string>} productSizeIds - Array of product size IDs
   * @returns {Promise} API response
   */
  async bulkDeleteProductSizes(productSizeIds) {
    try {
      // Convert IDs to integers and ensure they're in the correct format
      const idsAsIntegers = (productSizeIds || []).map(id => parseInt(id, 10)).filter(id => !isNaN(id));

      // API expects a top-level JSON array of ids in the request body, e.g. [2,3,4]
      const response = await productSizesAPI.post(`${BASE_URL}/bulk-delete`, idsAsIntegers);

      // Remove deleted ids from full-list caches
      const idSet = new Set(idsAsIntegers.map((id) => String(id)));
      updateFullListCachesWithModifier((arr) => arr.filter((ps) => !idSet.has(String(ps.id))));

      // Remove byId cache entries
      try {
        for (const id of idsAsIntegers) deleteCacheEntry(`byId::${id}`);
      } catch (e) { }

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: extractApiError(error),
      };
    }
  },

  /**
   * Export product sizes as Excel using the server-side endpoint
   * @param {Object} params - Optional filters (search)
   * @returns {Promise} API response with blob data
   */
  async exportProductSizesToExcel(params = {}) {
    try {
      const queryParams = new URLSearchParams();
      if (params.search) queryParams.append('search', params.search);
      const query = queryParams.toString();
      const url = `${BASE_URL}/export/download${query ? `?${query}` : ''}`;
      const response = await productSizesAPI.get(url, { responseType: 'blob' });
      return { success: true, data: response.data, headers: response.headers };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  /**
   * Import product sizes from Excel file
   * @param {File} file - Excel file containing product sizes data
   * @returns {Promise} API response with import results
   */
  async importProductSizesFromExcel(file) {
    try {
      const formData = new FormData();
      formData.append('file', file);

      // Use multipart/form-data for file upload
      const response = await productSizesAPI.post(`${BASE_URL}/import/excel`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      // Clear cache after successful import since new data was added
      this.clearCache();

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: extractApiError(error),
      };
    }
  },

  /**
   * Clear all cached product size data
   * Useful when you need to force refresh from server
   */
  clearCache() {
    try {
      // Clear memory cache
      // Memory keys are "all::…" / "byId::…" — the old substring match never hit, so
      // the in-memory list outlived clearCache() and kept serving stale rows (P1-4).
      memoryCache.clear();

      // Clear sessionStorage cache
      for (let i = sessionStorage.length - 1; i >= 0; i--) {
        const key = sessionStorage.key(i);
        if (key && key.startsWith(CACHE_PREFIX)) {
          sessionStorage.removeItem(key);
        }
      }
    } catch (e) {
      // ignore storage errors
    }
  }
};

// Cleared on logout together with the other services (policy C16).
registerClearable(() => productSizesService.clearCache());

export default productSizesService;
